import type {
  IBookingItem,
  IRawGuestBooking,
} from "@/src/shared/features/booking/types";

const WALL_CLOCK = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

/**
 * Python sends check-in/out as zone-less day labels ("2026-10-14 00:00:01").
 * The date helpers read them as UTC calendar days, the way Laravel sent them,
 * so tag them with Z; otherwise Asia/Dhaka would shift the day.
 */
const toUtcDay = (value?: string | null) =>
  value && WALL_CLOCK.test(value) ? `${value.replace(" ", "T")}Z` : value;

/**
 * Reshape a booking from the Python guest API (`GET /guest/bookings/{id}`)
 * into IBookingItem, so the booking page and its cards keep reading the same
 * fields. Child rooms get the same treatment.
 */
export function toBookingItem(booking: IRawGuestBooking): IBookingItem {
  const rooms = (booking.rooms ?? []).map(toBookingItem);
  const listing = booking.listing
    ? {
        ...booking.listing,
        // Python keeps the booking-time policy snapshot on the booking.
        cancellation: booking.cancellation ?? null,
        property_type: { slug: booking.listing.property_type_slug },
        showable_price: booking.listing.price,
      }
    : null;

  return {
    ...booking,
    from: toUtcDay(booking.from),
    to: toUtcDay(booking.to),
    price: booking.price ?? 0,
    total_payable: booking.total_payable ?? 0,
    paid: booking.paid ?? 0,
    discount: booking.discount ?? 0,
    more_nights_discount: booking.more_nights_discount ?? 0,
    status: booking.status ?? "",
    status_updated_at: booking.status_updated_at ?? null,
    host: booking.host
      ? { ...booking.host, image: booking.host.image_url }
      : { first_name: "", last_name: "", phone: null },
    guest: booking.guest
      ? { ...booking.guest, image: booking.guest.image_url }
      : booking.guest,
    images: booking.images ?? [],
    reviews: booking.reviews ?? [],
    listing,
    rooms,
    // Every other field already arrives under IBookingItem's name.
  } as unknown as IBookingItem;
}
